import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';

import {
  CreatableAutoComplete,
  SelectProps,
  useOptionalField,
} from '@app/form';
import { Theme } from '@app/theme';

import { Attendee, AttendeeDto, Feature, useMe } from '@app/api';
import { ActionMeta, MultiValueProps, ValueType } from 'react-select';
import { Chip, LockFeatureChip, useToast } from '@app/core';
import classnames from 'classnames';
import { useOrganizationMembers } from 'queries';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useFeatureRequiredCallback } from 'components';

const useStyles = createUseStyles((theme: Theme) => ({
  chip: {
    margin: theme.marginBase / 4,
    padding: 0,
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    width: 'fit-content',
    alignItems: 'stretch',
  },
  text: {
    lineHeight: '150%',
    padding: [
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase,
    ],
  },
  removeIcon: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.marginBase / 2,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  icon: {
    fontSize: theme.iconSize.small,
  },
  header: {
    marginTop: theme.marginBase * 2,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    marginBottom: theme.marginBase / 2,
  },
  title: {
    ...theme.fonts.label,
  },
  description: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    paddingBottom: theme.marginBase / 2,
  },
}));

interface OptionType {
  value: AttendeeDto;
  label: string;
  __isNew__?: boolean;
}

interface Props {
  organizationId: string;
  taskSettingId: string;
  planId?: string;
}

const MultiValue = (
  props: MultiValueProps<{ value: string; label: string }>,
) => {
  const tag = props.data.label;
  const classes = useStyles();
  return (
    <Chip
      color="green"
      className={classes.chip}
      {...props.innerProps}
      key={tag}
    >
      <p className={classes.text}>{tag}</p>
      <div className={classes.removeIcon} {...props.removeProps}>
        <i className={classnames('ri-close-line', classes.icon)} />
      </div>
    </Chip>
  );
};
const components = {
  MultiValue,
};

export const AttendeesSelect = ({
  organizationId,
  taskSettingId,
  planId,
  ...props
}: Props & SelectProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: membersOrga } = useOrganizationMembers(organizationId);

  const toast = useToast();
  const [disable, setDisable] = useState(true);

  const {
    field: { value },
    helpers,
  } = useOptionalField({ name: 'moderatedTaskSettings.attendees' });
  const selectedAttendees: OptionType[] = useMemo(() => {
    return value?.map((attendee: Attendee) => ({
      value: attendee,
      label: attendee.email,
    }));
  }, [value]);

  const members = useMemo(() => {
    return membersOrga?.filter((member) => member.account.id !== me?.id) || [];
  }, [me?.id, membersOrga]);

  const [inputValue, setInputValue] = useState('');

  const onTypeText = useCallback(
    (inputValue: string) => {
      if (disable) {
        return;
      }
      setInputValue(inputValue);
      if (inputValue.length === 0) {
        return;
      }
    },
    [disable],
  );

  const verifyEmail = useCallback(
    async (email: string) => {
      const schema = yup.object().shape({
        email: yup.string().email(),
      });

      const isValid = await schema.isValid({
        email: email,
      });

      if (!isValid) {
        toast.error(`${email} is not a valid email`);
        return false;
      }

      return isValid;
    },
    [toast],
  );

  const _onChange = useCallback(
    async (
      value: ValueType<OptionType, true>,
      action?: ActionMeta<OptionType>,
    ) => {
      if (disable) {
        return;
      }
      const newValue = value.find((value) => value.__isNew__);
      const newAttendees = value.filter(
        (value) => !value.__isNew__ && value.value,
      ) as OptionType[];
      if (newValue) {
        const valid = await verifyEmail(newValue.value.email);
        if (!valid) {
          return;
        }
        newAttendees.push({
          value: {
            email: newValue.label,
            taskSettingId,
          },
          label: newValue.label,
        });
      }

      const removedValue =
        action?.action === 'remove-value' && action?.removedValue?.value;
      const newValues =
        (removedValue
          ? selectedAttendees.filter((val) => removedValue !== val.value)
          : newAttendees) || [];

      if (helpers) {
        helpers.setValue(newValues.map((val) => val.value));
      }
      setInputValue('');

      return;
    },
    [disable, helpers, selectedAttendees, taskSettingId, verifyEmail],
  );

  const options: OptionType[] = useMemo(() => {
    return [
      ...members
        .filter(({ deletedAt }) => !deletedAt)
        .map((member) => ({
          value: {
            accountId: member.account.id,
            email: member.account.email,
            taskSettingId,
          },
          label: member.account.email,
        })),
    ];
  }, [members, taskSettingId]);

  const onTryToAddEmail = useFeatureRequiredCallback(
    Feature.MEETING_ATTENDEES,
    () => setDisable(false),
    planId,
  );

  return (
    <div onClick={onTryToAddEmail}>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <div className={classes.title}>
            {t('editSession.settings.attendees.label')}
          </div>
          <LockFeatureChip
            feature={Feature.MEETING_ATTENDEES}
            planId={planId}
          ></LockFeatureChip>
        </div>
        <div className={classes.description}>
          {t('editSession.settings.attendees.label_desc')}
        </div>
      </div>
      <CreatableAutoComplete<OptionType, true>
        {...props}
        options={options}
        small={false}
        value={selectedAttendees}
        components={components as any}
        isMulti
        placeholder={t('editSession.settings.attendees.placeHolder')}
        onChange={_onChange}
        inputValue={inputValue}
        onInputChange={onTypeText}
        noMargin
      />
    </div>
  );
};
